import { useState, useRef } from "react";
import { UploadSimple, CheckCircle, X, Camera } from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export const FileUpload = ({
  label,
  category,
  hint,
  required = false,
  onUploaded,
  initialFile = null,
  accept = "image/*,application/pdf,.heic,.heif",
  useCamera = false,
  testid,
}) => {
  const [file, setFile] = useState(initialFile);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(initialFile ? fileUrl(initialFile.storage_path) : null);
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const localUrl = URL.createObjectURL(f);
      setPreviewUrl(localUrl);
      const form = new FormData();
      form.append("file", f);
      form.append("category", category);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(data);
      onUploaded?.(data);
      toast.success("Datei hochgeladen");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload fehlgeschlagen";
      toast.error(`Upload fehlgeschlagen: ${msg}`);
      setPreviewUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
    onUploaded?.(null);
  };

  return (
    <div className="w-full">
      <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          data-testid={testid || `upload-${category}`}
          className="w-full border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/30 rounded-xl p-6 flex flex-col items-center justify-center gap-2 transition-colors min-h-[140px]"
        >
          {loading ? (
            <div className="text-slate-500 text-sm">Lade hoch…</div>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                {category.includes("vehicle") || category.includes("damage") ? (
                  <Camera size={24} className="text-slate-500" />
                ) : (
                  <UploadSimple size={24} className="text-slate-500" />
                )}
              </div>
              <div className="text-sm font-medium text-slate-700">Datei wählen oder Foto machen</div>
              {hint && <div className="text-xs text-slate-500 text-center">{hint}</div>}
            </>
          )}
        </button>
      ) : (
        <div className="relative border border-slate-200 rounded-xl p-3 bg-white flex items-center gap-3">
          {previewUrl && (file.content_type?.startsWith("image/") || file.content_type?.includes("image")) ? (
            <img src={previewUrl} alt="preview" className="w-16 h-16 object-cover rounded-lg" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
              <CheckCircle size={28} weight="fill" className="text-emerald-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{file.original_filename}</div>
            <div className="text-xs text-slate-500">{Math.round((file.size || 0) / 1024)} KB · hochgeladen</div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid={`remove-${category}`}
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        {...(useCamera ? { capture: "environment" } : {})}
        onChange={handleChange}
        className="hidden"
      />
    </div>
  );
};

export default FileUpload;
